import type { DetailsHTMLAttributes, ReactNode, SyntheticEvent } from "react";

import { cn } from "../utils";
import { NcIconChevronRight } from "./nc-icons";

interface NcCollapseProps extends Omit<DetailsHTMLAttributes<HTMLDetailsElement>, "open"> {
  children: ReactNode;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const NcCollapse = ({
  children,
  className,
  onOpenChange,
  onToggle: nativeOnToggle,
  isOpen,
  ...props
}: NcCollapseProps) => {
  const handleToggle = (e: SyntheticEvent<HTMLDetailsElement>) => {
    const el = e.currentTarget as HTMLDetailsElement;
    onOpenChange?.(el.open);
    if (nativeOnToggle) nativeOnToggle(e);
  };

  return (
    <details
      data-nc="NcCollapse"
      className={cn(
        // Margin left for content alignment
        "ml-[1.4375rem]",
        // Animate content height when open/closed
        "[interpolate-size:allow-keywords]",
        "[&::details-content]:h-0 [&::details-content]:overflow-clip [&::details-content]:transition-[height_0.25s,content-visibility_0.25s] [&::details-content]:transition-discrete",
        "open:[&::details-content]:h-auto",
        // Expanded margin bottom
        "open:[&>summary]:mb-2",
        // Rotated icon
        "open:[&>summary>svg]:rotate-90",
        className
      )}
      {...props}
      // If the `isOpen` prop it will control the element using the native open prop
      // otherwise the native uncontrolled behavior is preserved by omitting the prop.
      {...(typeof isOpen === "boolean" ? { open: isOpen } : {})}
      onToggle={handleToggle}
    >
      {children}
    </details>
  );
};

type NcCollapseSummary = DetailsHTMLAttributes<HTMLDetailsElement>;

const NcCollapseSummary = ({ children, className, ...props }: NcCollapseSummary) => {
  return (
    <summary
      data-nc="NcCollapse.Summary"
      className={cn(
        "ml-[-1.4375rem] flex max-w-max cursor-pointer items-center gap-1.5 rounded",
        "focus-visible:outline-main focus-visible:outline-2 focus-visible:outline-offset-2",
        // Hide default marker
        "[&::-webkit-details-marker]:hidden [&::marker]:hidden",
        className
      )}
      {...props}
    >
      <NcIconChevronRight className="text-ui-dark duration-fast shrink-0 text-[0.85em] transition-transform" />
      {children}
    </summary>
  );
};

NcCollapse.Summary = NcCollapseSummary;
